import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import './BannerSlot.css';
import TemplateRenderer from './templates/TemplateRenderer';
import {
	getDeviceType,
	isClosedByVisitor,
	isWithinSchedule,
	rememberClose,
} from './displayRules.helper';
import { getCountdownTarget } from './stickyBanner.helper';
import {
	SiteTimezone,
	StickyBannerSettingsPayload,
	TemplateDesign,
} from './types';

/**
 * Browser-side rules (device, schedule, visitor type, closed, countdown).
 * Location / login / role rules were already checked by PHP.
 *
 * @param banner       Banner.
 * @param design       Template of the banner.
 * @param siteTimezone Site timezone.
 * @param isReturning  Whether the visitor is returning.
 */
export const isEligibleInBrowser = (
	banner: StickyBannerSettingsPayload,
	design: TemplateDesign,
	siteTimezone: SiteTimezone,
	isReturning: boolean
): boolean => {
	const rules = banner.displayRules;
	const now = Date.now();

	if ( ! rules.devices[ getDeviceType( window.innerWidth ) ] ) {
		return false;
	}
	if ( ! isWithinSchedule( rules, siteTimezone, now ) ) {
		return false;
	}
	if ( rules.visitors.visitorType === 'new' && isReturning ) {
		return false;
	}
	if ( rules.visitors.visitorType === 'returning' && ! isReturning ) {
		return false;
	}
	if ( isClosedByVisitor( banner.id ) ) {
		return false;
	}
	// An expired countdown set to "hide" removes the banner, so let a
	// lower-priority banner take this position instead.
	if (
		design.components.countdown.visible &&
		banner.countdown.afterExpiry === 'hide'
	) {
		const target = getCountdownTarget( banner.countdown, siteTimezone );
		if ( target !== null && target <= now ) {
			return false;
		}
	}
	return true;
};

/** Height of the WP admin bar when it is fixed at the top. */
const getAdminBarOffset = (): number => {
	const bar = document.getElementById( 'wpadminbar' );
	if ( ! bar ) {
		return 0;
	}
	return window.getComputedStyle( bar ).position === 'fixed'
		? bar.offsetHeight
		: 0;
};

interface BannerSlotProps {
	banner: StickyBannerSettingsPayload;
	design: TemplateDesign;
	siteTimezone: SiteTimezone;
}

/**
 * Renders one banner on the live site: handles the trigger (delay /
 * scroll), sticky positioning, page padding and close memory.
 */
const BannerSlot: React.FC< BannerSlotProps > = ( {
	banner,
	design,
	siteTimezone,
} ) => {
	const { placement, trigger, closeBehavior } = banner.displayRules;
	const isFixed = placement.behavior === 'fixed';
	const isTop = placement.position === 'top';

	const [ triggered, setTriggered ] = useState(
		trigger.type === 'immediate'
	);
	const [ shown, setShown ] = useState( false );
	const [ topOffset, setTopOffset ] = useState( 0 );
	const slotRef = useRef< HTMLDivElement >( null );

	/* ----- Trigger ----- */
	useEffect( () => {
		if ( triggered ) {
			return;
		}
		if ( trigger.type === 'delay' ) {
			const timer = window.setTimeout(
				() => setTriggered( true ),
				trigger.delaySeconds * 1000
			);
			return () => window.clearTimeout( timer );
		}
		if ( trigger.type === 'scroll' ) {
			const check = () => {
				const scrollable =
					document.documentElement.scrollHeight - window.innerHeight;
				const percent =
					scrollable > 0
						? ( window.scrollY / scrollable ) * 100
						: 100;
				if ( percent >= trigger.scrollPercent ) {
					setTriggered( true );
				}
			};
			check();
			window.addEventListener( 'scroll', check, { passive: true } );
			return () => window.removeEventListener( 'scroll', check );
		}
	}, [
		triggered,
		trigger.type,
		trigger.delaySeconds,
		trigger.scrollPercent,
	] );

	// Animate in on the frame after mounting.
	useEffect( () => {
		if ( ! triggered ) {
			return;
		}
		const frame = window.requestAnimationFrame( () => setShown( true ) );
		return () => window.cancelAnimationFrame( frame );
	}, [ triggered ] );

	/* ----- Sticky top offset (WP admin bar) ----- */
	useEffect( () => {
		if ( ! isFixed || ! isTop ) {
			return;
		}
		const update = () => setTopOffset( getAdminBarOffset() );
		update();
		window.addEventListener( 'resize', update );
		return () => window.removeEventListener( 'resize', update );
	}, [ isFixed, isTop ] );

	/* ----- Push page content so a sticky bar never covers it ----- */
	useLayoutEffect( () => {
		const slot = slotRef.current;
		if ( ! slot || ! isFixed || ! placement.pushContent || ! triggered ) {
			return;
		}
		const property = isTop ? 'paddingTop' : 'paddingBottom';
		const original = document.body.style[ property ];
		const originalTransition = document.body.style.transition;
		document.body.style.transition = 'padding 0.3s ease';

		const apply = () => {
			document.body.style[ property ] = `${ slot.offsetHeight }px`;
		};
		apply();

		let observer: ResizeObserver | null = null;
		if ( typeof ResizeObserver !== 'undefined' ) {
			observer = new ResizeObserver( apply );
			observer.observe( slot );
		} else {
			window.addEventListener( 'resize', apply );
		}

		return () => {
			observer?.disconnect();
			window.removeEventListener( 'resize', apply );
			document.body.style[ property ] = original;
			document.body.style.transition = originalTransition;
		};
	}, [ isFixed, isTop, placement.pushContent, triggered ] );

	if ( ! triggered ) {
		return null;
	}

	const classes = [
		'wplms-sb-slot',
		isFixed ? 'is-fixed' : 'is-static',
		isTop ? 'is-top' : 'is-bottom',
		shown ? 'is-shown' : '',
	]
		.filter( Boolean )
		.join( ' ' );

	return (
		<div
			ref={ slotRef }
			className={ classes }
			data-banner-id={ banner.id }
			style={ {
				zIndex: placement.zIndex,
				top: isFixed && isTop ? topOffset : undefined,
			} }
		>
			<TemplateRenderer
				design={ design }
				content={ banner.content }
				countdown={ banner.countdown }
				siteTimezone={ siteTimezone }
				onClose={ () =>
					rememberClose( banner.id, closeBehavior.rememberHours )
				}
			/>
		</div>
	);
};

export default BannerSlot;
